import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Check } from 'lucide-react';
import { Section } from './ui/Section';
const bonuses = [{
  num: '1',
  title: 'El Mapa de 30 Minutos: Tu Negocio en Piloto Automático',
  value: '$27 USD',
  image: 'https://cdn.magicpatterns.com/uploads/kKtYEBmxsqiVjNSzMjuW3q/heylloyd-image-1773432330500.png',
  intro: 'El error que comete el 90% de las personas cuando decide automatizar su negocio es este:\n\nEmpiezan por lo más complejo.\n\nQuieren conectar 7 herramientas, hacer flows avanzados, crear secuencias de email… Y a los 3 días abandonan porque se perdieron en el proceso.\n\nEste bono existe para que eso no te pase.\n\nEn 30 minutos — sin configurar nada todavía — vas a tener:',
  bullets: ['Un mapa claro de dónde se te está yendo el tiempo real (sin adivinar)', 'Tus 3 automatizaciones de mayor impacto identificadas con precisión', 'Un plan de 7 días para ejecutar sin ansiedad y sin saltarte pasos'],
  extra: 'Incluye un workbook editable, una matriz de priorización y una checklist de 1 página que podés imprimir y tener al lado tuyo mientras arrancás.\n\nNo es motivación. No es teoría.\n\nEs el mapa que te dice exactamente dónde poner la primera piedra.',
  quote: 'La claridad vale más que la velocidad. Este bono te da las dos.'
}, {
  num: '2',
  title: 'La Biblioteca de Respuestas Blindadas (WhatsApp/DM)',
  value: '$37 USD',
  image: 'https://cdn.magicpatterns.com/uploads/mgkjZCqeFWCw2TLQfGfGcN/heylloyd-image-1773432433598.png',
  intro: 'Mientras tu sistema se configura… Tu WhatsApp sigue sonando.\n\nProspectos que preguntan el precio. Clientes que mandan "hola" a las 11pm. Leads que dicen "lo estoy viendo" y nunca más aparecen.\n\nY vos ahí, escribiendo párrafos enteros, justificando, explicando, perdiendo tiempo y energía en conversaciones que no cierran.\n\nEste bono te da 60 scripts listos para copiar y pegar. Están escritos para hombres que venden servicios digitales. Sin sonar robot. Sin sonar desesperado. Con autoridad y claridad.',
  bullets: ['Scripts para calificar en 3 preguntas sin interrogar', 'Scripts para enviar precio sin justificarte ni bajar el valor', 'Scripts para manejar "está caro" sin ponerte nervioso', 'Scripts para cerrar con 2 opciones (el método más efectivo para reducir fricción)', 'Scripts para reactivar leads fríos sin parecer desesperado', 'Scripts de límites y soporte post-compra (para que el cliente sepa las reglas)'],
  extra: 'También incluye el framework R.E.S.P. — el sistema de 4 pasos para responder cualquier mensaje en menos de 30 segundos con autoridad y sin pensar.\n\nMás un mini SOP de mensajes para que tu bandeja deje de ser un caos de conversaciones sin resolver.',
  quote: 'Tu WhatsApp no debería manejarte a vos. Este bono invierte la ecuación.'
}, {
  num: '3',
  title: 'El Kit Anti-Caos: Checklist de Entrega Perfecta',
  value: '$27 USD',
  image: 'https://cdn.magicpatterns.com/uploads/iAc77qKC8WcNch2vmT4FN3/heylloyd-image-1773432475049.png',
  intro: 'Hay un momento que nadie te cuenta cuando empezás a automatizar:\n\nEl momento en que el sistema trae más clientes… y vos seguís entregando como siempre.\n\nSin orden. Sin protocolo. Con revisiones de más. Con mensajes de soporte innecesarios. Con errores que te consumen tiempo que el sistema ya te había devuelto.\n\nEste kit cierra ese agujero.\n\nEs la checklist operativa que convierte tu entrega en un sistema repetible, consistente y profesional.',
  bullets: ['La cadena de entrega en 5 pasos (de la confirmación de pago al cierre)', 'Tu "Definición de Done": qué incluye, qué no incluye, cuántas revisiones, en qué tiempo', 'Checklist de control de calidad de 9 puntos antes de enviar cualquier entregable', 'Mensaje de cierre que reduce el soporte post-entrega a casi cero', 'Tracker semanal para detectar dónde se traba tu operación antes de que se convierta en problema'],
  extra: 'Incluye versión printable (1 página) y versión Notion para usar digital.',
  quote: 'Automatizar el ingreso sin ordenar la entrega es construir sobre arena. Este kit es el cemento.'
}];
export function BonusCardsSection() {
  return <Section className="px-6 md:px-12 max-w-6xl mx-auto" data-id="element-17">
      <div className="space-y-24" data-id="element-18">
        {bonuses.map((bonus, idx) => <motion.div key={bonus.num} initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7
      }} className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-start`} data-id="element-19">
            {/* Image Column */}
            <div className="w-full lg:w-5/12 flex-shrink-0" data-id="element-20">
              <div className="relative border border-[#d4a853]/20 p-2 bg-[#111827]/30 rounded-sm" data-id="element-21">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#b8860b]/10 to-transparent opacity-50" data-id="element-22" />
                <img src={bonus.image} alt={bonus.title} className="w-full h-auto object-cover relative z-10 shadow-2xl" data-id="element-23" />
              </div>
            </div>

            {/* Content Column */}
            <div className="w-full lg:w-7/12 space-y-6" data-id="element-24">
              <div className="inline-flex items-center space-x-2 bg-[#d4a853]/10 border border-[#d4a853]/30 px-4 py-2 rounded-sm" data-id="element-25">
                <Gift className="w-4 h-4 text-[#d4a853]" data-id="element-26" />
                <span className="font-mono text-[#d4a853] text-sm font-bold tracking-widest uppercase" data-id="element-27">
                  BONO #{bonus.num} — GRATIS
                </span>
              </div>

              <h3 className="font-serif text-3xl md:text-4xl font-bold text-gray-100 leading-tight" data-id="element-28">
                {bonus.title}
              </h3>

              <p className="font-mono text-sm text-gray-400 uppercase tracking-wide" data-id="element-29">
                Valorado en{' '}
                <span className="text-[#f0c674]" data-id="element-30">{bonus.value}</span> — Tuyo sin
                costo adicional
              </p>

              <div className="w-12 h-[1px] bg-[#d4a853]/50 my-6" data-id="element-31" />

              <div className="font-sans text-gray-300 space-y-4 leading-relaxed whitespace-pre-line" data-id="element-32">
                {bonus.intro}
              </div>

              <ul className="space-y-4 py-4" data-id="element-33">
                {bonus.bullets.map((bullet, i) => <li key={i} className="flex items-start" data-id="element-34">
                    <Check className="w-5 h-5 text-[#d4a853] mt-1 mr-3 flex-shrink-0" data-id="element-35" />
                    <span className="font-sans text-gray-200" data-id="element-36">{bullet}</span>
                  </li>)}
              </ul>

              <div className="font-sans text-gray-400 space-y-4 leading-relaxed whitespace-pre-line" data-id="element-37">
                {bonus.extra}
              </div>

              <div className="mt-8 p-6 border-l-2 border-[#d4a853] bg-gradient-to-r from-[#111827] to-transparent" data-id="element-38">
                <p className="font-serif text-xl text-[#f0c674] italic" data-id="element-39">
                  "{bonus.quote}"
                </p>
              </div>
            </div>
          </motion.div>)}
      </div>
    </Section>;
}